<template>
  <div class="page">
    <div class="toolbar">
      <el-form :inline="true" class="toolbar-form" @submit.prevent>
        <el-form-item label="开始时间">
          <el-date-picker
            v-model="startDate"
            type="datetime"
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="开始日期"
            :clearable="false"
            class="date-short"
            :style="{ width: '180px' }"
          />
        </el-form-item>

        <el-form-item label="结束时间">
          <el-date-picker
            v-model="endDate"
            type="datetime"
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="结束日期"
            :clearable="false"
            class="date-short"
            :style="{ width: '180px' }"
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="status" size="default">
            <el-radio-button :label="''">全部</el-radio-button>
            <el-radio-button :label="0">跟踪</el-radio-button>
            <el-radio-button :label="1">成功</el-radio-button>
            <el-radio-button :label="2">失败</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="搜索">
          <el-input
            v-model="search"
            placeholder="请输入关键字"
            clearable
            @keyup.enter="onQuery"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="loading" @click="onQuery">查询</el-button>
          <el-button type="success" :loading="pulling" @click="onPull">拉取</el-button>
          <el-button type="warning" :loading="processing" @click="onProcess">处理</el-button>
          <el-button type="info" @click="onLogout">退出登录</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-card class="table-card" shadow="never">
      <el-table :data="rows" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="90" />
        <el-table-column prop="billNo" label="单号" min-width="140" />
        <el-table-column prop="logComCode" label="物流公司" min-width="120" />
        <el-table-column prop="carryBillNo" label="运单号" min-width="140" />
        <el-table-column prop="phone" label="电话" width="120" />
        <el-table-column label="状态" width="100">
          <template #default="scope">
            <el-tag :type="statusType(scope.row.status)">
              {{ statusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
        <el-table-column prop="createTime" label="拉取时间" width="170" />
        <el-table-column prop="updateTime" label="更新时间" width="170" />
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="scope">
            <el-button
              type="primary"
              link
              :loading="retryingId === scope.row.id"
              :disabled="retryingId === scope.row.id"
              @click="onRetry(scope.row)"
            >
              重试
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 500, 1000, 2000]"
          @current-change="onPageChange"
          @size-change="onPageSizeChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { ElMessageBox } from 'element-plus'
import { apiUrl } from '../api/config'

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatDate(date) {
  if (!date) return ''
  const d = new Date(date)
  const y = d.getFullYear()
  const m = pad2(d.getMonth() + 1)
  const day = pad2(d.getDate())
  const hh = pad2(d.getHours())
  const mm = pad2(d.getMinutes())
  const ss = pad2(d.getSeconds())
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
}

function getMonthStart(date = new Date()) {
  const d = new Date(date)
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0)
}

function getMonthEnd(date = new Date()) {
  const d = new Date(date)
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)
}

function getAuthHeaders() {
  const token = localStorage.getItem('token')
  const headers = {
    Accept: 'application/json'
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

function handleAuthError(res) {
  if (res.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('userName')
    localStorage.removeItem('expireAt')
    window.location.reload()
    throw new Error('登录已过期，请重新登录')
  }
}

async function requestJson(urlOrPath) {
  const url = /^https?:\/\//i.test(urlOrPath) ? urlOrPath : apiUrl(urlOrPath)
  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  })

  handleAuthError(res)

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status} ${text}`)
  }

  return await res.json()
}

async function postJson(urlOrPath, payload) {
  const url = /^https?:\/\//i.test(urlOrPath) ? urlOrPath : apiUrl(urlOrPath)
  const headers = getAuthHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload ?? {})
  })

  handleAuthError(res)

  const text = await res.text().catch(() => '')
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${text}`)
  }

  try {
    return text ? JSON.parse(text) : null
  } catch {
    throw new Error(text || '响应不是合法 JSON')
  }
}

async function postNumber(urlOrPath, value) {
  const url = /^https?:\/\//i.test(urlOrPath) ? urlOrPath : apiUrl(urlOrPath)
  const headers = getAuthHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(Number(value))
  })

  handleAuthError(res)

  const text = await res.text().catch(() => '')
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${text}`)
  }

  try {
    return text ? JSON.parse(text) : null
  } catch {
    throw new Error(text || '响应不是合法 JSON')
  }
}

export default {
  name: 'LogisticsList',
  emits: ['logout'],
  setup(_, { emit }) {
    const loading = ref(false)
    const pulling = ref(false)
    const processing = ref(false)
    const retryingId = ref(null)

    // 默认区间：本月1号到本月底
    const startDate = ref(getMonthStart())
    const endDate = ref(getMonthEnd())

    // 状态筛选：''=全部，0=跟踪，1=成功，2=失败
    const status = ref('')
    const search = ref('')

    const page = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const rows = ref([])

    const statusLabel = (s) => {
      if (s === 0) return '跟踪'
      if (s === 1) return '成功'
      if (s === 2) return '失败'
      return String(s ?? '')
    }

    const statusType = (s) => {
      if (s === 0) return 'info'
      if (s === 1) return 'success'
      if (s === 2) return 'danger'
      return 'info'
    }

    const queryString = computed(() => {
      const params = new URLSearchParams({
        startTime: formatDate(startDate.value),
        endTime: formatDate(endDate.value),
        page: String(page.value),
        pageSize: String(pageSize.value)
      })

      if (status.value !== '' && status.value !== null && status.value !== undefined) {
        params.set('status', String(status.value))
      }

      if (search.value !== '' && search.value !== null && search.value !== undefined) {
        params.set('search', String(search.value))
      }

      return params.toString()
    })

    const fetchList = async () => {
      loading.value = true
      try {
        const url = `/Api/OutStock/GetLogisticsrPage?${queryString.value}`
        const json = await requestJson(url)

        if (json?.code !== 0) {
          throw new Error(json?.message || '接口返回失败')
        }

        total.value = json?.data?.total ?? 0
        rows.value = json?.data?.list ?? []
      } catch (e) {
        ElMessage.error(e?.message || '查询失败')
      } finally {
        loading.value = false
      }
    }

    const onQuery = async () => {
      page.value = 1
      await fetchList()
    }

    const onPull = async () => {
      if (pulling.value) return
      pulling.value = true
      try {
        const startTime = formatDate(startDate.value)
        const endTime = formatDate(endDate.value)

        if (!startTime || !endTime) {
          ElMessage.error('请选择开始时间和结束时间')
          return
        }

        const json = await postJson('/Api/OutStock/PullOutStockLogistics', {
          startTime,
          endTime
        })

        if (Number(json?.code) !== 0) {
          throw new Error(json?.message || '拉取失败')
        }

        ElMessage.success(json?.data || json?.message || '操作成功')
        await fetchList()
      } catch (e) {
        ElMessage.error(e?.message || '拉取失败')
      } finally {
        pulling.value = false
      }
    }

    const onProcess = async () => {
      if (processing.value) return
      processing.value = true
      try {
        const startTime = formatDate(startDate.value)
        const endTime = formatDate(endDate.value)

        if (!startTime || !endTime) {
          ElMessage.error('请选择开始时间和结束时间')
          return
        }

        const json = await postJson('/Api/OutStock/BatchUploadLogistics', {
          startTime,
          endTime
        })

        if (Number(json?.code) !== 0) {
          throw new Error(json?.message || '处理失败')
        }

        ElMessage.success(json?.data || json?.message || '操作成功')
        await fetchList()
      } catch (e) {
        ElMessage.error(e?.message || '处理失败')
      } finally {
        processing.value = false
      }
    }

    const onRetry = async (row) => {
      try {
        const id = row?.id
        if (id === null || id === undefined || id === '') {
          ElMessage.error('缺少 ID，无法重试')
          return
        }

        if (Number(row?.status) === 1) {
          try {
            await ElMessageBox.confirm(
              '该条物流当前状态为“成功”，确认要重试吗？',
              '二次确认',
              {
                confirmButtonText: '确认重试',
                cancelButtonText: '取消',
                type: 'warning'
              }
            )
          } catch {
            return
          }
        }

        if (retryingId.value !== null) return
        retryingId.value = id

        const json = await postNumber('/Api/OutStock/UploadLogistics', id)
        if (Number(json?.code) !== 0) {
          throw new Error(json?.message || '重试失败')
        }

        ElMessage.success(json?.data || json?.message || '操作成功')
        await fetchList()
      } catch (e) {
        ElMessage.error(e?.message || '重试失败')
      } finally {
        retryingId.value = null
      }
    }

    const onPageChange = async (p) => {
      page.value = p
      await fetchList()
    }

    const onPageSizeChange = async (s) => {
      pageSize.value = s
      page.value = 1
      await fetchList()
    }

    const onLogout = () => {
      emit('logout')
    }

    onMounted(() => {
      fetchList()
    })

    return {
      loading,
      pulling,
      processing,
      retryingId,
      startDate,
      endDate,
      status,
      search,
      page,
      pageSize,
      total,
      rows,
      statusLabel,
      statusType,
      onQuery,
      onPull,
      onProcess,
      onRetry,
      onPageChange,
      onPageSizeChange,
      onLogout
    }
  }
}
</script>

<style scoped>
.page {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  padding: 16px;
  background:
    radial-gradient(780px 420px at 100% 0%, rgba(159, 201, 255, 0.24) 0%, rgba(159, 201, 255, 0) 68%),
    radial-gradient(640px 360px at 0% 100%, rgba(164, 231, 197, 0.2) 0%, rgba(164, 231, 197, 0) 70%),
    linear-gradient(180deg, #f7fbff 0%, #f2f7fb 100%);
}

.toolbar {
  position: relative;
  z-index: 1;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(244, 250, 255, 0.7);
  border: 1px solid rgba(208, 224, 241, 0.72);
}

.toolbar-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
}

.date-short {
  width: 120px;
}

.table-card {
  position: relative;
  z-index: 1;
  border-radius: 10px;
  background: rgba(246, 251, 255, 0.78);
  border: 1px solid rgba(203, 221, 240, 0.74);
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 26px rgba(64, 101, 146, 0.09);
}

.table-card :deep(.el-card__body) {
  background: rgba(248, 252, 255, 0.7);
}

.table-card :deep(.el-table) {
  --el-table-bg-color: rgba(250, 253, 255, 0.82);
  --el-bg-color: rgba(250, 253, 255, 0.82);
  --el-table-tr-bg-color: rgba(250, 253, 255, 0.82);
  --el-fill-color-lighter: rgba(241, 247, 253, 0.92);
  --el-table-row-hover-bg-color: rgba(226, 239, 252, 0.65);
}

.table-card :deep(.el-table th.el-table__cell) {
  background: rgba(233, 243, 252, 0.88);
  color: #1f3a55;
}

.table-card :deep(.el-table tr) {
  background: rgba(250, 253, 255, 0.82);
}

.table-card :deep(.el-table__row--striped td.el-table__cell) {
  background: rgba(244, 249, 254, 0.9);
}

.table-card :deep(.el-table__row:hover > td.el-table__cell) {
  background: rgba(226, 239, 252, 0.65) !important;
}

.pager {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
}
</style>
